import React, {useEffect, useState} from 'react';
import { Search, RotateCcw, FileText, UploadCloud, DollarSign, CheckCircle, Clock, MessageSquare, Send, Printer } from 'lucide-react';
import api from '../services/api.js';
import { useAuth } from '../App.jsx';
import IndiceDocumentosModal from '../components/IndiceDocumentosModal.jsx';
import SolicitudDocViewer from '../components/SolicitudDocViewer.jsx';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

const CONTENEDOR_STYLE = {
  padding: '2rem clamp(16px, 4vw, 48px)',
};

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1rem',
};

const ACCIONES_STYLE = {
  display: 'flex',
  gap: '0.75rem',
  flexWrap: 'wrap',
};

const PAGINACION_STYLE = {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const PAGE_BUTTON_STYLE = (activa) => ({
  background: activa ? 'var(--copper)' : 'rgba(255,255,255,0.06)',
  color: '#fff',
  border: activa
    ? '1px solid var(--copper)'
    : '1px solid rgba(255,255,255,0.1)',
});

function getNombreCompleto(registro) {
  return [
    registro.apellido_paterno,
    registro.apellido_materno,
    registro.primer_nombre,
    registro.segundo_nombre,
  ]
    .filter(Boolean)
    .join(' ');
}

function renderValor(valor) {
  return valor ?? '—';
}

export default function EstadoSolicitud() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { isStaff } = useAuth();
  const location = useLocation();

  const [numeroSeguimiento, setNumeroSeguimiento] = useState('');
  const [rutSolicitante, setRutSolicitante] = useState('');
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [error, setError] = useState('');
  const [modalRegistro, setModalRegistro] = useState(null);
  const [comentarioPago, setComentarioPago] = useState('');
  const [archivoPago, setArchivoPago] = useState(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Auto-llenado desde URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const r = params.get('rut');
    const s = params.get('seguimiento');
    const token = params.get('token');
    if (r) setRutSolicitante(r);
    if (s) setNumeroSeguimiento(s);
    if (token && !solicitud && !loading) {
      cargarPorToken(token);
    }
  }, [location.search, executeRecaptcha]);

  const cargarPorToken = async (token) => {
    setSearching(true);
    setError('');
    try {
      const { data } = await api.get(`/solicitudes/seguimiento-token/${token}`);
      setSolicitud(data);
      // Opcional: rellenar campos para que el usuario sepa qué está viendo
      setRutSolicitante(data.solicitud.rut_solicitante);
      setNumeroSeguimiento(data.solicitud.numero_seguimiento);
    } catch (err) {
      setError(err.response?.data?.error || 'Token de respuesta inválido o expirado.');
    } finally {
      setSearching(false);
    }
  };

  // Si cambian rut o seguimiento por URL, buscar automáticamente si ambos están presentes
  useEffect(() => {
    if (rutSolicitante && numeroSeguimiento && !solicitud && !loading) {
      // Pequeño retardo para asegurar que reCAPTCHA está listo
      const timer = setTimeout(() => {
        if (executeRecaptcha) {
          buscarSolicitud();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [rutSolicitante, numeroSeguimiento, executeRecaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await buscarSolicitud();
  };

  const buscarSolicitud = async () => {
    const numTrim = numeroSeguimiento.trim();
    const rutTrim = rutSolicitante.trim();

    if (!numTrim || !rutTrim) {
      setError('Debes ingresar el número de seguimiento y tu RUT.');
      return;
    }

    if (!executeRecaptcha) {
      setError('ReCAPTCHA no está listo aún.');
      return;
    }

    const normalizar = (r) => {
      if (!r) return r;
      let limpio = r.replace(/\./g, '').replace(/\s/g, '');
      if (!limpio.includes('-') && limpio.length > 1) {
        return limpio.slice(0, -1) + '-' + limpio.slice(-1);
      }
      return limpio;
    };

    const recaptchaToken = await executeRecaptcha('estado_solicitud');

    setSearching(true);
    setError('');
    setSolicitud(null);

    try {
      const rutSanitizado = normalizar(rutTrim);
      const isArchivero = numTrim.startsWith('ARCH-');
      const url = isArchivero ? '/archivero/seguimiento' : '/solicitudes/seguimiento';

      const { data } = await api.post(url,
        { numero_seguimiento: numTrim, rut_solicitante: rutSanitizado },
        { headers: { 'X-Recaptcha-Token': recaptchaToken } }
      );

      if (isArchivero) {
        setSolicitud({ solicitud: data, is_archivero: true });
      } else {
        setSolicitud(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'No se encontró la solicitud.');
    } finally {
      setSearching(false);
    }
  };

  const handleSubirComprobanteForm = async (e) => {
    e.preventDefault();
    if (!archivoPago || !comentarioPago.trim()) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('comprobante', archivoPago);
      if (comentarioPago.trim()) {
        fd.append('comentario', comentarioPago.trim());
      }
      fd.append('rut_solicitante', rutSolicitante);
      fd.append('numero_seguimiento', numeroSeguimiento);

      const isArchivero = solicitud.is_archivero;
      const url = isArchivero
        ? `/archivero/${solicitud.solicitud.numero_seguimiento}/comprobante`
        : `/solicitudes/${solicitud.solicitud.numero_seguimiento}/comprobante`;

      await api.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Comprobante subido exitosamente.');
      setArchivoPago(null);
      setComentarioPago('');
      buscarSolicitud(); // Recargar datos
    } catch (err) {
      toast.error(err.response?.data?.errors?.comprobante?.[0] || err.response?.data?.error || err.response?.data?.message || 'Error al subir el comprobante.');
    } finally {
      setUploading(false);
    }
  };

  const handleEnviarComentario = async (e) => {
    e.preventDefault();
    if (!comentario.trim()) return;

    setEnviandoComentario(true);
    try {
      const isArchivero = solicitud.is_archivero;
      const url = isArchivero
        ? `/archivero/${solicitud.solicitud.numero_seguimiento}/comentar`
        : `/solicitudes/${solicitud.solicitud.numero_seguimiento}/comentar`;

      await api.post(url, {
        mensaje: comentario,
        comentario,
        rut_solicitante: rutSolicitante,
        numero_seguimiento: numeroSeguimiento
      });
      toast.success('Comentario enviado al funcionario.');
      setComentario('');
      await buscarSolicitud();
    } catch (err) {
      toast.error('Error al enviar comentario.');
    } finally {
      setEnviandoComentario(false);
    }
  };

  const limpiar = () => {
    setNumeroSeguimiento('');
    setRutSolicitante('');
    setSolicitud(null);
    setError('');
  };

  return (
    <div className="container" style={CONTENEDOR_STYLE}>
      <div className="animate-fade-in">
        <h2 className="section-title">Estado de Solicitud</h2>
        <span className="accent-line" />
        <p className="section-subtitle">
          Busca con tu número de seguimiento y RUT el estado de tu solicitud de documentos.
        </p>
      </div>

      <div className="glass-card animate-fade-in" style={{ marginBottom: '2rem' }}>
        <form onSubmit={handleSubmit}>
          <div style={GRID_STYLE}>
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="rut_solicitante">RUT solicitante</label>
              <input
                id="rut_solicitante"
                name="rut_solicitante"
                value={rutSolicitante}
                onChange={(e) => setRutSolicitante(e.target.value)}
                placeholder="12.345.678-9"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="numero_seguimiento">Número de seguimiento</label>
              <input
                id="numero_seguimiento"
                name="numero_seguimiento"
                value={numeroSeguimiento}
                onChange={(e) => setNumeroSeguimiento(e.target.value)}
                placeholder="SOL-12345"
              />
            </div>
          </div>

          <div style={{ ...ACCIONES_STYLE, marginTop: '1rem' }}>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={limpiar}
              title="Limpiar búsqueda"
            >
              <RotateCcw size={15} /> Limpiar
            </button>

            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner" /> Buscando...
                </>
              ) : (
                <>
                  <Search size={16} /> Buscar
                </>
              )}
            </button>
          </div>


          {error && (
            <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
              ⚠️  Registro no encontrado.
            </div>
          )}
        </form>
      </div>

      {solicitud && (() => {
        const estadoNombre = solicitud.solicitud.estado?.nombre || 'Desconocido';

        return (
        <div className="animate-fade-in">
          <div className="glass-card" style={{ marginBottom: '1.5rem' }}>
            <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
              <strong>Número:</strong> {solicitud.solicitud.numero_seguimiento}
            </p>
            <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
              <strong>Solicitante:</strong> {solicitud.solicitud.nombre_solicitante}
            </p>
            {solicitud.solicitud.telefono && (
              <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
                <strong>Teléfono:</strong> {solicitud.solicitud.telefono}
              </p>
            )}
            {solicitud.solicitud.email && (
              <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
                <strong>Email:</strong> {solicitud.solicitud.email}
              </p>
            )}
            {solicitud.solicitud.rut_boleta && (
              <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
                <strong>RUT para Boleta:</strong> {solicitud.solicitud.rut_boleta}
              </p>
            )}
            <p className="text-muted" style={{ marginBottom: 0 }}>
              <strong>Estado:</strong> {estadoNombre}
            </p>
          </div>

          {/* Documentos del Expediente (subidos por el funcionario) - MOVIDO ARRIBA PARA MAYOR VISIBILIDAD */}
          {['Completado', 'Pago Confirmado', 'Comprobante Pago Recibido'].includes(estadoNombre) &&
           Array.isArray(solicitud?.solicitud?.documentos) && solicitud.solicitud.documentos.length > 0 && (
            <div className="glass-card animate-fade-in" style={{
              marginBottom: '1.5rem',
              padding: '1.4rem 1.6rem',
              borderLeft: `4px solid ${estadoNombre === 'Completado' ? 'var(--primary)' : 'var(--copper)'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
                {estadoNombre === 'Completado' ? (
                  <CheckCircle size={20} color="var(--primary)" />
                ) : (
                  <FileText size={20} color="var(--copper)" />
                )}
                <h3 style={{ fontSize: '1.1rem', color: 'var(--primary-dark)', margin: 0 }}>
                  {estadoNombre === 'Completado' ? 'Documentos Listos para Descarga' : 'Documentos Adicionales del Expediente'}
                </h3>
              </div>
              <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', marginBottom: '1.2rem' }}>
                {estadoNombre === 'Completado'
                  ? 'El equipo del CBR ha procesado y adjuntado los siguientes documentos finales a tu solicitud:'
                  : 'El equipo del CBR ha adjuntado los siguientes documentos adicionales a tu expediente:'}
              </p>
              <SolicitudDocViewer
                docs={solicitud.solicitud.documentos}
                titulo="Archivos Adjuntos"
                emptyText="Sin documentos adicionales."
                isArchivero={solicitud.is_archivero}
              />
            </div>
          )}



          {solicitud.is_archivero ? (
            <div className="glass-card animate-fade-in" style={{ marginBottom: '1.5rem', padding: '1.5rem', borderLeft: '4px solid var(--copper)' }}>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--primary-dark)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={20} color="var(--copper)" /> Detalle del Archivero
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <strong style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Tipo de Solicitud</strong>
                  <span>{solicitud.solicitud.tipo_solicitud === 'escritura' ? 'Copia de Escritura Pública' : 'Documento Protocolizado'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Notaría</strong>
                  <span>{solicitud.solicitud.notaria === 'primera_san_carlos' ? 'Primera Notaría San Carlos' : 'Segunda Notaría San Carlos'}</span>
                </div>
                <div>
                  <strong style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Año</strong>
                  <span>{solicitud.solicitud.anio}</span>
                </div>
                {solicitud.solicitud.tipo_solicitud === 'escritura' ? (
                  <div>
                    <strong style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Número de Repertorio</strong>
                    <span>{solicitud.solicitud.repertorio}</span>
                  </div>
                ) : (
                  <div>
                    <strong style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Número de Protocolización</strong>
                    <span>{solicitud.solicitud.numero_protocolizacion}</span>
                  </div>
                )}
              </div>
            </div>
          ) : Array.isArray(solicitud?.solicitud?.items) && solicitud.solicitud.items.length > 0 ? (
              <div className="table-wrapper" style={{marginBottom: '1.5rem'}}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                  <tr>
                    <th>Nombre/Referencia</th>
                    <th>Estado / Trámite</th>
                    <th style={{ textAlign: 'center' }}>Foja</th>
                    <th style={{ textAlign: 'center' }}>Núm.</th>
                    <th style={{ textAlign: 'center' }}>Año</th>
                    <th>Propiedad / Ubicación</th>
                    <th style={{ textAlign: 'center' }}>Comuna</th>
                    {isStaff() && <th>Acciones</th>}
                  </tr>
                  </thead>
                  <tbody>
                  {solicitud.solicitud.items.map((registro) => (
                    <tr key={registro.id}>
                      <td>
                        {registro.indice ? (
                          <strong>{getNombreCompleto(registro.indice)}</strong>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{registro.nombre_propietario || '—'}</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span className="badge badge-info" style={{ width: 'fit-content', fontSize: '0.7rem' }}>
                            {registro.tipo_documento}
                          </span>
                        </div>
                      </td>
                      <td className="text-muted" style={{ textAlign: 'center' }}>{renderValor(registro.foja)}</td>
                      <td className="text-muted" style={{ textAlign: 'center' }}>{renderValor(registro.numero)}</td>
                      <td className="text-muted" style={{ textAlign: 'center' }}>{renderValor(registro.anio)}</td>
                      <td style={{maxWidth: 220, fontSize: '0.8rem'}}>
                        {registro.indice && renderValor(registro.indice.descripcion_propiedad)}
                      </td>
                      <td className="text-muted" style={{ textAlign: 'center' }}>
                        {registro.indice ? renderValor(registro.indice.comuna) : '—'}
                      </td>
                      {isStaff() && (
                          <td>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setModalRegistro(registro)}
                                title="Gestionar documentos"
                                style={{padding: '0.4rem', whiteSpace: 'nowrap'}}
                            >
                              <UploadCloud size={16}/> Docs
                            </button>
                          </td>
                      )}
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          ) : (
            <div className="glass-card" style={{textAlign: 'center', padding: '3rem'}}>
              <FileText size={48} style={{color: 'var(--gray-600)', marginBottom: '1rem'}}/>
              <p className="text-muted">Esta solicitud no tiene registros asociados todavía.</p>
            </div>
          )}

          {/* ── SECCIÓN DE COTIZACIÓN ── */}
          {solicitud?.solicitud?.total_cotizacion > 0 && (
            <div className="glass-card animate-fade-in print-section" style={{ marginBottom: '1.5rem', borderLeft: '4px solid var(--success)', background: '#f0fdf4' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.1rem', color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <DollarSign size={20} color="var(--success)" /> Detalle de Cotización
                </h3>
                <button className="btn btn-secondary btn-sm no-print" onClick={() => window.print()}>
                  <Printer size={16} /> Imprimir
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.2rem' }}>
                {!solicitud.is_archivero ? (
                  <>
                    {solicitud.solicitud.items.map(item => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#374151', borderBottom: '1px dashed #d1fae5', paddingBottom: 4 }}>
                        <span>• {item.tipo_documento}</span>
                        <strong style={{ marginLeft: 10 }}>${new Intl.NumberFormat('es-CL').format(item.valor || 0)}</strong>
                      </div>
                    ))}
                    {solicitud.solicitud.cotizacion_extras?.map((extra, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#374151', borderBottom: '1px dashed #d1fae5', paddingBottom: 4 }}>
                        <span>• {extra.descripcion}</span>
                        <strong style={{ marginLeft: 10 }}>${new Intl.NumberFormat('es-CL').format(extra.valor || 0)}</strong>
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#374151', borderBottom: '1px dashed #d1fae5', paddingBottom: 4 }}>
                    <span>• {solicitud.solicitud.tipo_solicitud === 'escritura' ? 'Copia de Escritura Pública' : 'Documento Protocolizado'}</span>
                    <strong style={{ marginLeft: 10 }}>${new Intl.NumberFormat('es-CL').format(solicitud.solicitud.total_cotizacion)}</strong>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '2px solid #d1fae5' }}>
                  <strong>TOTAL A PAGAR</strong>
                  <strong style={{ color: 'var(--success)' }}>${new Intl.NumberFormat('es-CL').format(solicitud.solicitud.total_cotizacion)}</strong>
                </div>
                
                {solicitud.solicitud.observaciones_cotizacion && (
                  <div style={{ marginTop: '1rem', padding: '1rem', background: '#e0f2fe', borderRadius: '8px', borderLeft: '4px solid #0284c7' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#0369a1' }}>Observación del Funcionario:</h4>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#0c4a6e', whiteSpace: 'pre-wrap' }}>
                      {solicitud.solicitud.observaciones_cotizacion}
                    </p>
                  </div>
                )}

                {/* Instrucciones de Pago idénticas al correo de cotización recepcionada */}
                {estadoNombre === 'Pendiente Pago' && solicitud?.solicitud?.total_cotizacion > 0 && (
                  <div style={{
                    marginTop: '1.2rem',
                    backgroundColor: '#fffaf0',
                    padding: '1.2rem',
                    borderRadius: '8px',
                    borderLeft: '4px solid #d4af37',
                    border: '1px solid #fef3c7',
                    color: '#4a5568',
                    fontSize: '0.9rem',
                    lineHeight: '1.6'
                  }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 'bold', color: '#1a1e38' }}>
                      Para dar curso a su solicitud debe enviar un correo electrónico a:
                    </p>
                    <p style={{ margin: '0 0 10px', color: '#1a1e38' }}>
                      <a href="mailto:conservadorsancarlos@hotmail.com" style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}>
                        conservadorsancarlos@hotmail.com
                      </a> titulado <strong>DEPÓSITO {solicitud.solicitud.numero_seguimiento}</strong>,
                    </p>
                    <p style={{ margin: 0 }}>
                      acompañando copia del depósito o de la transferencia electrónica a la cuenta corriente número <strong>N° 56998023</strong> del Banco BCI, a nombre de <strong>don Raúl Hernán Fuentes Jiménez</strong>, RUT <strong>13.793.838-3</strong>. También puede adjuntar su comprobante en la sección inferior.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Subida de Comprobante (solo si hay cotización) */}
          {solicitud?.solicitud?.total_cotizacion > 0 && (
            <div className="glass-card animate-fade-in no-print" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
              <h4 style={{ fontSize: '0.9rem', marginBottom: '0.6rem', color: 'var(--primary-dark)' }}>Comprobante de Pago / transferencia</h4>
              
              {['Pago Confirmado', 'Comprobante Pago Recibido', 'Pagado'].includes(estadoNombre) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--success)', fontWeight: 600, fontSize: '0.85rem' }}>
                  <CheckCircle size={16} /> ¡Pago confirmado! Estamos procesando tus documentos para la entrega final.
                </div>
              ) : solicitud.solicitud.comprobante_pago_path ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--primary)', fontWeight: 600, fontSize: '0.85rem' }}>
                  <Clock size={16} /> Comprobante en revisión. Te avisaremos cuando el pago sea confirmado.
                </div>
              ) : (
                <form onSubmit={handleSubirComprobanteForm} style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                    Una vez realizada la transferencia, sube aquí tu comprobante (PDF o Imagen, Máx. 10MB). <strong>Indica los datos para la emisión de la boleta electrónica</strong>.
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: '0.78rem', padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      onClick={() => {
                        const datos = [
                          `Nombre: ${solicitud.solicitud.nombre_solicitante || ''}`,
                          `RUT: ${solicitud.solicitud.rut_boleta || solicitud.solicitud.rut_solicitante || ''}`,
                          `Email: ${solicitud.solicitud.email || ''}`,
                          solicitud.solicitud.telefono ? `Teléfono: ${solicitud.solicitud.telefono}` : null
                        ].filter(Boolean).join('\n');
                        setComentarioPago(datos);
                      }}
                    >
                      📋 Usar datos por defecto de la solicitud
                    </button>
                    {solicitud.solicitud.rut_boleta && solicitud.solicitud.rut_boleta !== solicitud.solicitud.rut_solicitante && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ fontSize: '0.75rem', padding: '3px 8px', background: 'rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' }}
                        onClick={() => setComentarioPago(prev => prev ? `${prev}\nRUT Boleta: ${solicitud.solicitud.rut_boleta}` : `RUT Boleta: ${solicitud.solicitud.rut_boleta}`)}
                      >
                        + RUT Boleta ({solicitud.solicitud.rut_boleta})
                      </button>
                    )}
                  </div>

                  <textarea 
                    rows={3} 
                    className="form-control" 
                    placeholder="Indica datos para la boleta (Nombre completo o Razón Social, RUT, Correo electrónico, etc.)..." 
                    value={comentarioPago} 
                    onChange={e => setComentarioPago(e.target.value)} 
                    style={{ fontSize: '0.85rem' }} 
                    disabled={uploading}
                    required
                  />
                  <input type="file" accept="image/*,application/pdf" onChange={e => setArchivoPago(e.target.files[0])} disabled={uploading} required />
                  <button type="submit" className="btn btn-primary btn-sm" disabled={uploading || !archivoPago || !comentarioPago.trim()} style={{ alignSelf: 'flex-start' }}>
                    {uploading ? <><span className="spinner" style={{ width: 14, height: 14 }}/> Subiendo...</> : 'Subir Comprobante'}
                  </button>
                </form>
              )}
            </div>
          )}
          {/* ── SECCIÓN DE MENSAJES Y COMUNICACIÓN ── */}
          {solicitud && (
            <div className="glass-card animate-fade-in" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--primary-dark)', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <MessageSquare size={20} color="var(--primary)" /> Mensajes y Notificaciones
              </h3>

              {/* Hilo de mensajes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                {(solicitud.solicitud.workflow_steps || solicitud.solicitud.workflowSteps)?.length > 0 ? (
                  (solicitud.solicitud.workflow_steps || solicitud.solicitud.workflowSteps).map((step) => {
                    const isFuncionario = !!step.worker_id;
                    const isPago = step.payload?.tipo === 'comprobante_pago';
                    
                    return (
                      <div key={step.id} style={{
                        alignSelf: isFuncionario ? 'flex-start' : 'flex-end',
                        maxWidth: '85%',
                        background: isFuncionario ? '#f3f4f6' : 'var(--primary)',
                        color: isFuncionario ? '#374151' : '#ffffff',
                        padding: '0.8rem 1rem',
                        borderRadius: '12px',
                        borderTopLeftRadius: isFuncionario ? '2px' : '12px',
                        borderTopRightRadius: isFuncionario ? '12px' : '2px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '1rem' }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.8 }}>
                            {isFuncionario ? 'Funcionario' : 'Tú'}
                          </span>
                          <span style={{ fontSize: '0.65rem', opacity: 0.6 }}>
                            {new Date(step.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        </div>
                        
                        {isPago && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: isFuncionario ? 'var(--success)' : '#d1fae5', fontSize: '0.75rem', fontWeight: 700, marginBottom: 4 }}>
                            <DollarSign size={12} /> Comprobante de Pago Subido
                          </div>
                        )}

                        {step.payload?.asunto && (
                          <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 2 }}>{step.payload.asunto}</div>
                        )}

                        <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                          {step.payload?.mensaje || step.payload?.comentario}
                        </div>

                        {step.payload?.archivos?.length > 0 && (
                          <div style={{ 
                            marginTop: '0.5rem', 
                            fontSize: '0.78rem', 
                            background: isFuncionario ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.15)',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: isFuncionario ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.2)'
                          }}>
                            <div style={{ fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>📎</span> Archivos adjuntos:
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '1.1rem', listStyleType: 'disc' }}>
                              {step.payload.archivos.map((archivo, idx) => (
                                <li key={idx} style={{ wordBreak: 'break-all' }}>{archivo}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted" style={{ textAlign: 'center', fontSize: '0.85rem', padding: '1rem' }}>
                    No hay mensajes todavía.
                  </p>
                )}
              </div>

              {/* Responder */}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
                <h4 style={{ fontSize: '0.9rem', marginBottom: '0.8rem', color: 'var(--primary-dark)' }}>Responder o enviar duda:</h4>
                <form onSubmit={handleEnviarComentario} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <textarea 
                    rows={3} 
                    className="form-control" 
                    placeholder="Escribe tu mensaje aquí..." 
                    value={comentario} 
                    onChange={e => setComentario(e.target.value)} 
                    style={{ fontSize: '0.85rem', background: '#fff' }} 
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={enviandoComentario || !comentario.trim()} style={{ borderRadius: '20px', padding: '0.5rem 1.2rem' }}>
                      {enviandoComentario ? <span className="spinner" style={{ width: 14, height: 14 }}/> : <><Send size={14} style={{ marginRight: 6 }}/> Enviar Respuesta</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {modalRegistro && (
          <IndiceDocumentosModal
              indice={modalRegistro}
              onClose={() => setModalRegistro(null)}
          />
      )}
    </div>
  );
}